import { ReactNode, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SuggestionsGrid, SuggestionCard, SuggestionIconBadge } from "./ChatMessage.styled";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT, INSIGHTS_TEXT } from "./tokens";
import {
  PendenciasIcon,
  PerformanceIcon,
  AnomaliasIcon,
  UltimaExportacaoIcon,
  ExecutivoIcon,
} from "./insightsIcons";
import {
  getAnomalias,
  getInsightsErrorMessage,
  getPendenciasDoDia,
  getPerformance,
  getResumoExecutivo,
  getUltimaExportacao,
  IInsightsPrefabKind,
  IInsightsPrefabResponse,
} from "v2/services/insights.service";

interface PrefabButtonConfig {
  id: IInsightsPrefabKind;
  icon: ReactNode;
  label: string;
  enabled: boolean;
}

const PREFAB_ICON_SX = { color: `${CONDUCCO_PRIMARY} !important`, fontSize: 18 };

// Todos desligados até a reconciliação ser corrigida.
//
// A versão anterior deste comentário dizia que "Última exportação" usava fonte
// confiável e por isso continuava ativa. Não usa: `SP_RESULTADO_EXPORTACAO` lê
// `DBCONDUCCO.PUBLIC.DT_MOVIMENTACOES`, a mesma tabela de
// `SP_PENDENCIAS_DIA` e `SP_PERFORMANCE_MES` (conferido com `DESC PROCEDURE`
// em 2026-08-12). Ela conta `STATUS='RETAINED'` na coluna RETIDAS, que é
// exatamente o status que o bug infla — o percentual de sucesso sai
// subestimado pelo mesmo motivo que derrubou os outros quatro.
//
// Dois problemas independentes do bug, para quando forem religados:
// o rótulo promete uma exportação e a SP devolve as cinco últimas execuções
// (`LIMIT 5` por `DAG_RUN_ID`); e `EXPORTADAS` conta `STATUS='DONE'` da
// `MASSA_MOVIMENTADA`, ou seja, o que o painel registrou como concluído — não
// confirmação de que a operadora recebeu o arquivo.
//
// `investigar-cpf` saiu daqui: a investigação por CPF é o relatório
// `timeline-cpf`, na aba Relatórios, onde a lista de CPFs é validada de
// verdade (dígito verificador, duplicata, teto do backend). Ter os dois
// caminhos deixava o atalho do chat como a porta pior para a mesma pergunta.
const PREFAB_BUTTONS: PrefabButtonConfig[] = [
  { id: "pendencias", icon: <PendenciasIcon sx={PREFAB_ICON_SX} />, label: "Pendências do dia", enabled: false },
  { id: "performance", icon: <PerformanceIcon sx={PREFAB_ICON_SX} />, label: "Performance", enabled: false },
  { id: "anomalias", icon: <AnomaliasIcon sx={PREFAB_ICON_SX} />, label: "Anomalias", enabled: false },
  { id: "exportacao", icon: <UltimaExportacaoIcon sx={PREFAB_ICON_SX} />, label: "Última exportação", enabled: false },
  { id: "resumo-executivo", icon: <ExecutivoIcon sx={PREFAB_ICON_SX} />, label: "Resumo executivo", enabled: false },
];

interface ChatSuggestionsProps {
  workspaceId: string;
  disabled?: boolean;
  onResult: (label: string, response: IInsightsPrefabResponse) => void;
  onError: (message: string) => void;
}

export function ChatSuggestions({
  workspaceId,
  disabled,
  onResult,
  onError,
}: ChatSuggestionsProps) {
  const [loadingId, setLoadingId] = useState<IInsightsPrefabKind | null>(null);

  const visible = PREFAB_BUTTONS.filter((button) => button.enabled);

  const runPrefab = async (id: IInsightsPrefabKind) => {
    setLoadingId(id);
    try {
      let response: IInsightsPrefabResponse;
      switch (id) {
        case "pendencias":
          response = await getPendenciasDoDia(workspaceId);
          break;
        case "performance":
          response = await getPerformance(workspaceId);
          break;
        case "anomalias":
          response = await getAnomalias(workspaceId);
          break;
        case "exportacao":
          response = await getUltimaExportacao(workspaceId);
          break;
        case "resumo-executivo":
          response = await getResumoExecutivo(workspaceId);
          break;
        // `investigar-cpf` continua existindo no serviço (o relatório
        // `timeline-cpf` usa a mesma rota); só não tem atalho aqui.
        default:
          return;
      }
      const label = PREFAB_BUTTONS.find((button) => button.id === id)!.label;
      onResult(label, response);
    } catch (error: any) {
      onError(getInsightsErrorMessage(error, "Erro ao processar solicitação"));
    } finally {
      setLoadingId(null);
    }
  };

  if (visible.length === 0) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <SuggestionsGrid>
        {visible.map((button) => (
          <SuggestionCard
            key={button.id}
            role="button"
            tabIndex={disabled || loadingId ? -1 : 0}
            onClick={() => {
              if (disabled || loadingId) return;
              runPrefab(button.id);
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (disabled || loadingId) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                runPrefab(button.id);
              }
            }}
            sx={{
              opacity: disabled || loadingId ? 0.5 : 1,
              pointerEvents: disabled || loadingId ? "none" : "auto",
            }}
          >
            <SuggestionIconBadge>
              {loadingId === button.id ? (
                <CircularProgress size={14} sx={{ color: CONDUCCO_PRIMARY }} />
              ) : (
                button.icon
              )}
            </SuggestionIconBadge>
            <Typography
              sx={{
                fontFamily: INSIGHTS_FONT,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: INSIGHTS_TEXT,
                textAlign: "left",
              }}
            >
              {button.label}
            </Typography>
          </SuggestionCard>
        ))}
      </SuggestionsGrid>
    </Box>
  );
}
